<template>
  <div class="relative min-h-screen flex bg-[#0a0f18] font-sans overflow-hidden">
    <canvas ref="canvasRef" class="absolute inset-0 z-0 no-print"></canvas>

    <aside class="relative z-10 w-64 bg-[#1e2532]/95 backdrop-blur-md text-white flex flex-col shadow-2xl border-r border-gray-700 no-print">
      <div class="p-6 border-b border-gray-700">
        <h1 class="text-xl font-extrabold text-white">관리자 시스템</h1>
        <p class="text-xs text-gray-400 mt-1">Global Meridian Cycle</p>
      </div>
      <nav class="flex-1 overflow-y-auto py-4">
        <ul class="space-y-1 px-3">
          <li v-for="menu in menuList" :key="menu">
            <a href="#" 
               @click.prevent="currentMenu = menu"
               :class="['block px-4 py-3 rounded-lg text-sm font-bold transition-colors', 
                        menu === currentMenu ? 'bg-blue-600 text-white shadow-md' : 'text-gray-300 hover:bg-gray-700 hover:text-white']">
              {{ menu }}
            </a>
          </li>
        </ul>
      </nav>
      <div class="p-4 border-t border-gray-700">
        <router-link to="/" class="block text-center text-xs text-gray-400 hover:text-white">회원 화면으로 이동</router-link>
      </div>
    </aside>

    <main class="relative z-10 flex-1 p-8 overflow-y-auto h-screen print-area">
      <div class="max-w-7xl mx-auto">
        <h2 class="text-2xl font-extrabold text-white mb-6 drop-shadow-md no-print">{{ currentMenu }}</h2>
        
        <div v-if="['입금대기', '승인완료내역', '회원관리', '출금관리', '수당관리'].includes(currentMenu)" class="bg-white/95 backdrop-blur-sm p-6 rounded-xl shadow-2xl border border-gray-200 print-bg-white">
          
          <div class="flex justify-between items-end mb-4 border-b pb-4 no-print">
            <h3 class="text-lg font-bold text-gray-800">{{ currentMenu }} 목록</h3>
            <div class="flex space-x-2">
              <button @click="exportExcel" class="text-xs bg-green-600 hover:bg-green-700 text-white font-bold py-1.5 px-3 rounded transition-colors shadow-sm">
                엑셀 저장 ⬇
              </button>
              <button @click="printPage" class="text-xs bg-gray-600 hover:bg-gray-700 text-white font-bold py-1.5 px-3 rounded transition-colors shadow-sm">
                프린트 🖨
              </button>
              <button @click="refreshData" class="text-xs bg-gray-200 hover:bg-gray-300 text-gray-700 font-bold py-1.5 px-3 rounded transition-colors shadow-sm">
                새로고침 ↻
              </button>
            </div>
          </div>
          
          <h3 class="hidden print-block text-xl font-bold text-black mb-4 text-center">{{ currentMenu }} 내역</h3>

          <div class="flex space-x-3 mb-4 bg-gray-50 p-3 rounded-lg border border-gray-200 no-print">
            <div class="flex-1">
              <label class="block text-xs font-bold text-gray-600 mb-1">검색어 (이름/이메일/UID)</label>
              <input type="text" v-model="searchQuery" placeholder="검색어 입력" class="w-full px-3 py-1.5 border rounded text-sm focus:outline-none focus:border-blue-500" />
            </div>
            <div v-if="['입금대기', '승인완료내역', '회원관리'].includes(currentMenu)" class="flex-1">
              <label class="block text-xs font-bold text-gray-600 mb-1">센터</label>
              <select v-model="searchCenter" class="w-full px-3 py-1.5 border rounded text-sm focus:outline-none focus:border-blue-500">
                <option value="">전체 센터</option>
                <option v-for="c in centers" :key="c.name" :value="c.name">{{ c.name }}</option>
              </select>
            </div>
            <div v-if="currentMenu === '출금관리'" class="flex-1">
              <label class="block text-xs font-bold text-gray-600 mb-1">상태 필터</label>
              <select v-model="searchStatus" class="w-full px-3 py-1.5 border rounded text-sm focus:outline-none focus:border-blue-500">
                <option value="">전체 상태</option>
                <option value="PENDING">대기중</option>
                <option value="HOLD">보류됨</option>
                <option value="COMPLETED">지급완료</option>
                <option value="CANCELLED">취소됨(반환)</option>
              </select>
            </div>
            <div class="flex-1">
              <label class="block text-xs font-bold text-gray-600 mb-1">일자 필터</label>
              <input type="date" v-model="searchDate" class="w-full px-3 py-1.5 border rounded text-sm focus:outline-none focus:border-blue-500" />
            </div>
            <div class="flex items-end">
              <button @click="resetSearch" class="px-4 py-1.5 bg-gray-200 text-gray-700 font-bold text-sm rounded hover:bg-gray-300 transition-colors">초기화</button>
            </div>
          </div>

          <p v-if="isLoading" class="text-sm text-blue-600 font-bold mb-4 no-print">데이터를 불러오는 중입니다...</p>
          <p v-else-if="filteredData.length === 0" class="text-sm text-gray-500 font-bold py-8 text-center bg-gray-50 rounded-lg no-print">
            검색된 내역이 없습니다.
          </p>

          <div v-else class="overflow-x-auto print-overflow-visible">
            <table class="w-full text-left border-collapse whitespace-nowrap print-table">
              
              <template v-if="currentMenu === '입금대기' || currentMenu === '승인완료내역'">
                <thead>
                  <tr class="bg-gray-100 text-gray-700 text-xs uppercase font-extrabold select-none">
                    <th class="p-3 border-b cursor-pointer hover:bg-gray-200" @click="sortBy(currentMenu === '입금대기' ? 'createdAtTime' : 'approvedAtTime')">
                      {{ currentMenu === '입금대기' ? '신청일시' : '승인일시' }} <span v-show="sortKey===(currentMenu === '입금대기' ? 'createdAtTime' : 'approvedAtTime')">{{ sortOrder==='asc'?'▲':'▼' }}</span>
                    </th>
                    <th class="p-3 border-b cursor-pointer hover:bg-gray-200" @click="sortBy('name')">이름 (이메일) <span v-show="sortKey==='name'">{{ sortOrder==='asc'?'▲':'▼' }}</span></th>
                    <th class="p-3 border-b cursor-pointer hover:bg-gray-200" @click="sortBy('phone')">연락처 <span v-show="sortKey==='phone'">{{ sortOrder==='asc'?'▲':'▼' }}</span></th>
                    <th class="p-3 border-b cursor-pointer hover:bg-gray-200" @click="sortBy('center')">센터 <span v-show="sortKey==='center'">{{ sortOrder==='asc'?'▲':'▼' }}</span></th>
                    <th class="p-3 border-b cursor-pointer hover:bg-gray-200" @click="sortBy('level')">패키지 <span v-show="sortKey==='level'">{{ sortOrder==='asc'?'▲':'▼' }}</span></th>
                    <th class="p-3 border-b text-right cursor-pointer hover:bg-gray-200" @click="sortBy('totalPrice')">금액 <span v-show="sortKey==='totalPrice'">{{ sortOrder==='asc'?'▲':'▼' }}</span></th>
                    <th v-if="currentMenu === '입금대기'" class="p-3 border-b text-center no-print">관리</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in filteredData" :key="item.id" class="border-b hover:bg-gray-50 text-sm">
                    <td class="p-3 text-gray-500 font-mono text-xs">{{ formatDate(currentMenu === '입금대기' ? item.createdAt : item.approvedAt) }}</td>
                    <td class="p-3 font-bold text-gray-800">{{ item.name }} <div class="text-[10px] text-gray-400 font-normal">{{ item.email }}</div></td>
                    <td class="p-3 text-gray-700">{{ item.phone }}</td>
                    <td class="p-3 text-gray-700">{{ item.center }}</td>
                    <td class="p-3 font-bold text-blue-700">{{ item.level }} <span class="font-normal text-gray-500">x{{ item.quantity || 1 }}</span></td>
                    <td class="p-3 text-right font-extrabold">{{ formatNumber(item.totalPrice || item.price) }}원</td>
                    <td v-if="currentMenu === '입금대기'" class="p-3 text-center space-x-1 no-print">
                      <button @click="approveRequest(item)" class="bg-blue-600 text-white text-xs font-bold py-1 px-2 rounded hover:bg-blue-700 shadow-sm transition-colors">승인</button>
                      <button @click="rejectRequest(item.id)" class="bg-red-50 text-red-600 border border-red-200 text-xs font-bold py-1 px-2 rounded hover:bg-red-100 transition-colors">거절</button>
                    </td>
                  </tr>
                </tbody>
              </template>

              <template v-else-if="currentMenu === '회원관리'">
                <thead>
                  <tr class="bg-gray-100 text-gray-700 text-xs uppercase font-extrabold select-none">
                    <th class="p-3 border-b cursor-pointer hover:bg-gray-200" @click="sortBy('createdAtTime')">가입일 <span v-show="sortKey==='createdAtTime'">{{ sortOrder==='asc'?'▲':'▼' }}</span></th>
                    <th class="p-3 border-b cursor-pointer hover:bg-gray-200" @click="sortBy('name')">이름 (UID) <span v-show="sortKey==='name'">{{ sortOrder==='asc'?'▲':'▼' }}</span></th>
                    <th class="p-3 border-b cursor-pointer hover:bg-gray-200" @click="sortBy('phone')">연락처 (이메일) <span v-show="sortKey==='phone'">{{ sortOrder==='asc'?'▲':'▼' }}</span></th>
                    <th class="p-3 border-b cursor-pointer hover:bg-gray-200" @click="sortBy('recommender')">추천인 <span v-show="sortKey==='recommender'">{{ sortOrder==='asc'?'▲':'▼' }}</span></th>
                    <th class="p-3 border-b cursor-pointer hover:bg-gray-200" @click="sortBy('supporter')">후원인 <span v-show="sortKey==='supporter'">{{ sortOrder==='asc'?'▲':'▼' }}</span></th>
                    <th class="p-3 border-b cursor-pointer hover:bg-gray-200" @click="sortBy('center')">센터 / 직급 <span v-show="sortKey==='center'">{{ sortOrder==='asc'?'▲':'▼' }}</span></th>
                    <th class="p-3 border-b cursor-pointer hover:bg-gray-200" @click="sortBy('personalPV')">본인 PV <span v-show="sortKey==='personalPV'">{{ sortOrder==='asc'?'▲':'▼' }}</span></th>
                    <th class="p-3 border-b text-right cursor-pointer hover:bg-gray-200" @click="sortBy('availableBalance')">출금 가능 잔액 <span v-show="sortKey==='availableBalance'">{{ sortOrder==='asc'?'▲':'▼' }}</span></th>
                    <th class="p-3 border-b text-center no-print">관리</th>
                  </tr>
                </thead>
<tbody>
  <tr v-for="item in filteredData" :key="item.uid" 
      class="border-b text-sm transition-colors"
      :class="item.isActive === false ? 'bg-gray-200/60 opacity-60 grayscale' : 'hover:bg-gray-50'">
    
    <td class="p-3 text-gray-500 font-mono text-xs">{{ formatDate(item.createdAt) }}</td>
    
    <td class="p-3 font-bold" :class="item.isActive === false ? 'text-gray-500' : 'text-gray-800'">
      <div class="flex items-center space-x-1">
        <span>{{ item.name }}</span>
        <span v-if="item.isActive === false" class="bg-gray-500 text-white text-[9px] px-1.5 py-0.5 rounded font-black">계정정지</span>
      </div>
      <div class="text-[10px] text-gray-400 font-normal font-mono">{{ item.uid }}</div>
    </td>
    
    <td class="p-3 text-gray-700">
      {{ item.phone || '-' }}
      <div class="text-[10px] text-gray-500">{{ item.email }}</div>
    </td>
    
    <td class="p-3">
      <div class="font-bold text-sm" :class="item.isActive === false ? 'text-gray-500' : 'text-gray-800'">{{ item.recommenderName || '-' }}</div>
      <div class="text-[10px] text-gray-500 font-mono truncate max-w-[120px]">{{ item.recommender || '' }}</div>
    </td>
    
    <td class="p-3">
      <div class="font-bold text-sm" :class="item.isActive === false ? 'text-gray-500' : 'text-gray-800'">{{ item.supporterName || '-' }}</div>
      <div class="text-[10px] text-gray-500 font-mono truncate max-w-[120px]">{{ item.supporter || '' }}</div>
    </td>
    
    <td class="p-3 text-gray-700">
      {{ item.center }} 
      <span class="text-[10px] px-1 rounded ml-1" :class="item.isActive === false ? 'bg-gray-300 text-gray-600' : 'bg-blue-100 text-blue-700'">{{ item.level }}</span>
    </td>
    
    <td class="p-3 text-right font-bold" :class="item.isActive === false ? 'text-gray-500' : 'text-blue-600'">{{ formatNumber(item.personalPV) }} PV</td>
    <td class="p-3 text-right font-bold" :class="item.isActive === false ? 'text-gray-500' : 'text-green-600'">{{ formatNumber(item.availableBalance) }} 원</td>
    
    <td class="p-3 text-center no-print">
      <button @click="openUserEditModal(item)" class="bg-white text-gray-700 border border-gray-300 text-xs font-bold py-1.5 px-3 rounded hover:bg-gray-100 transition-colors shadow-sm">
        수정
      </button>
    </td>
  </tr>
</tbody>
              </template>

              <template v-else-if="currentMenu === '출금관리'">
                <thead>
                  <tr class="bg-gray-100 text-gray-700 text-xs uppercase font-extrabold select-none">
                    <th class="p-3 border-b cursor-pointer hover:bg-gray-200" @click="sortBy('createdAtTime')">신청일시 <span v-show="sortKey==='createdAtTime'">{{ sortOrder==='asc'?'▲':'▼' }}</span></th>
                    <th class="p-3 border-b cursor-pointer hover:bg-gray-200" @click="sortBy('name')">회원 정보 (이름/UID) <span v-show="sortKey==='name'">{{ sortOrder==='asc'?'▲':'▼' }}</span></th>
                    <th class="p-3 border-b cursor-pointer hover:bg-gray-200" @click="sortBy('phone')">연락처 / 이메일 <span v-show="sortKey==='phone'">{{ sortOrder==='asc'?'▲':'▼' }}</span></th>
                    <th class="p-3 border-b cursor-pointer hover:bg-gray-200" @click="sortBy('recommenderName')">추천인 <span v-show="sortKey==='recommenderName'">{{ sortOrder==='asc'?'▲':'▼' }}</span></th>
                    <th class="p-3 border-b cursor-pointer hover:bg-gray-200" @click="sortBy('accountInfo')">출금 계좌 정보 <span v-show="sortKey==='accountInfo'">{{ sortOrder==='asc'?'▲':'▼' }}</span></th>
                    <th class="p-3 border-b text-right cursor-pointer hover:bg-gray-200" @click="sortBy('amount')">신청액 / 최종 입금액 <span v-show="sortKey==='amount'">{{ sortOrder==='asc'?'▲':'▼' }}</span></th>
                    <th class="p-3 border-b text-center cursor-pointer hover:bg-gray-200" @click="sortBy('status')">상태 <span v-show="sortKey==='status'">{{ sortOrder==='asc'?'▲':'▼' }}</span></th>
                    <th class="p-3 border-b text-center no-print">관리</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in filteredData" :key="item.id" class="border-b hover:bg-gray-50 text-sm">
                    <td class="p-3 text-gray-500 font-mono text-xs">{{ formatDate(item.createdAt) }}</td>
                    <td class="p-3 font-bold text-gray-800">
                      {{ item.name }}
                      <div class="text-[10px] text-gray-400 font-mono font-normal mt-0.5">{{ item.uid }}</div>
                    </td>
                    <td class="p-3 text-gray-700 text-xs">
                      {{ item.phone || '-' }}
                      <div class="text-[10px] text-gray-500 mt-0.5">{{ item.email }}</div>
                    </td>
                    <td class="p-3 text-gray-700 text-xs font-bold">{{ item.recommenderName || '-' }}</td>
                    <td class="p-3 text-gray-700 text-xs">{{ item.accountInfo || '계좌정보 없음' }}</td>
                    <td class="p-3 text-right">
                      <div class="font-extrabold text-gray-800">{{ formatNumber(item.amount) }}원</div>
                      <div class="text-xs font-black text-blue-600 mt-0.5">입금: {{ formatNumber(item.netAmount || (item.amount * 0.95)) }}원</div>
                    </td>
                    <td class="p-3 text-center">
                      <span :class="{
                        'text-yellow-700 bg-yellow-100': item.status === 'PENDING',
                        'text-orange-700 bg-orange-100': item.status === 'HOLD',
                        'text-green-700 bg-green-100': item.status === 'COMPLETED',
                        'text-red-700 bg-red-100': item.status === 'CANCELLED' || item.status === 'DELETED'
                      }" class="px-2 py-1 text-[10px] font-bold rounded">
                        {{ item.status === 'PENDING' ? '대기중' : (item.status === 'HOLD' ? '보류됨' : (item.status === 'COMPLETED' ? '지급완료' : '반환됨')) }}
                      </span>
                    </td>
                    <td class="p-3 text-center no-print">
                      <div class="flex flex-col space-y-1 items-center">
                        <button v-if="item.status === 'PENDING' || item.status === 'HOLD'" @click="completeWithdrawal(item.id)" class="w-20 bg-blue-600 text-white text-[10px] font-bold py-1 px-2 rounded hover:bg-blue-700 shadow-sm transition-colors">지급완료</button>
                        <button v-if="item.status === 'PENDING'" @click="holdWithdrawal(item.id)" class="w-20 bg-orange-50 text-orange-600 border border-orange-200 text-[10px] font-bold py-1 px-2 rounded hover:bg-orange-100 transition-colors">보류</button>
                        <button v-if="item.status === 'PENDING' || item.status === 'HOLD'" @click="refundWithdrawal(item, '취소', 'CANCELLED')" class="w-20 bg-red-50 text-red-600 border border-red-200 text-[10px] font-bold py-1 px-2 rounded hover:bg-red-100 transition-colors">취소(반환)</button>
                        <button @click="refundWithdrawal(item, '삭제', 'DELETED', true)" class="w-20 bg-gray-200 text-gray-600 text-[10px] font-bold py-1 px-2 rounded hover:bg-gray-300 transition-colors">삭제</button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </template>

              <template v-else-if="currentMenu === '수당관리'">
                <thead>
                  <tr class="bg-gray-100 text-gray-700 text-xs uppercase font-extrabold select-none">
                    <th class="p-3 border-b cursor-pointer hover:bg-gray-200" @click="sortBy('createdAtTime')">발생일시 <span v-show="sortKey==='createdAtTime'">{{ sortOrder==='asc'?'▲':'▼' }}</span></th>
                    <th class="p-3 border-b cursor-pointer hover:bg-gray-200" @click="sortBy('receiverName')">수령 회원 (이름/UID) <span v-show="sortKey==='receiverName'">{{ sortOrder==='asc'?'▲':'▼' }}</span></th>
                    <th class="p-3 border-b cursor-pointer hover:bg-gray-200" @click="sortBy('receiverEmail')">이메일 <span v-show="sortKey==='receiverEmail'">{{ sortOrder==='asc'?'▲':'▼' }}</span></th>
                    <th class="p-3 border-b cursor-pointer hover:bg-gray-200" @click="sortBy('receiverPhone')">연락처 <span v-show="sortKey==='receiverPhone'">{{ sortOrder==='asc'?'▲':'▼' }}</span></th>
                    <th class="p-3 border-b cursor-pointer hover:bg-gray-200" @click="sortBy('recommenderName')">추천인 <span v-show="sortKey==='recommenderName'">{{ sortOrder==='asc'?'▲':'▼' }}</span></th>
                    <th class="p-3 border-b cursor-pointer hover:bg-gray-200" @click="sortBy('bonusType')">보너스 종류 <span v-show="sortKey==='bonusType'">{{ sortOrder==='asc'?'▲':'▼' }}</span></th>
                    <th class="p-3 border-b cursor-pointer hover:bg-gray-200" @click="sortBy('description')">상세 설명 <span v-show="sortKey==='description'">{{ sortOrder==='asc'?'▲':'▼' }}</span></th>
                    <th class="p-3 border-b text-right cursor-pointer hover:bg-gray-200" @click="sortBy('amount')">지급액(클릭 시 수정) <span v-show="sortKey==='amount'">{{ sortOrder==='asc'?'▲':'▼' }}</span></th>
                    <th class="p-3 border-b text-center cursor-pointer hover:bg-gray-200" @click="sortBy('status')">상태 <span v-show="sortKey==='status'">{{ sortOrder==='asc'?'▲':'▼' }}</span></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in filteredData" :key="item.id" class="border-b hover:bg-gray-50 text-sm">
                    <td class="p-3 text-gray-500 font-mono text-xs">{{ formatDate(item.createdAt) }}</td>
                    <td class="p-3 font-bold text-gray-800">{{ item.receiverName }}<div class="text-[10px] text-gray-400 font-normal font-mono">{{ item.uid }}</div></td>
                    <td class="p-3 text-gray-600 text-xs">{{ item.receiverEmail }}</td>
                    <td class="p-3 text-gray-600 text-xs">{{ item.receiverPhone || '-' }}</td>
                    <td class="p-3 font-bold text-gray-700 text-xs">{{ item.recommenderName }}</td>
                    <td class="p-3"><span class="px-2 py-1 bg-blue-50 text-blue-700 text-[10px] font-bold rounded">{{ item.bonusType }}</span></td>
                    <td class="p-3 text-gray-500 text-xs">{{ item.description }}</td>
                    <td class="p-3 text-right">
                      <button @click="openBonusModal(item)" class="font-extrabold text-green-600 hover:text-green-800 hover:underline transition-colors flex items-center justify-end w-full">
                        +{{ formatNumber(item.amount) }}원
                        <svg class="w-3 h-3 ml-1 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"></path></svg>
                      </button>
                    </td>
                    <td class="p-3 text-center">
                      <span class="px-2 py-1 text-[10px] font-bold rounded" :class="item.status === 'PENDING' ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'">
                        {{ item.status || '완료' }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </template>
            </table>
          </div>
        </div>

        <div v-else-if="currentMenu === '센터관리'" class="bg-white/95 backdrop-blur-sm p-6 rounded-xl shadow-2xl border border-gray-200 min-h-[600px]">
          <h3 class="text-lg font-bold text-gray-800 mb-4 border-b pb-2">센터(영업소) 관리 및 직책 지정</h3>
          
          <div class="flex space-x-2 mb-6">
            <input type="text" v-model="newCenterName" @keyup.enter="addCenter" class="flex-1 px-4 py-2 bg-[#f0f4f8] border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" placeholder="신규 센터명 입력" :disabled="isLoading" />
            <button @click="addCenter" :disabled="isLoading" class="bg-blue-800 text-white font-bold py-2 px-6 rounded-lg hover:bg-blue-700 transition-colors text-sm shadow-md disabled:opacity-50">새 센터 추가</button>
          </div>

          <p v-if="isLoading" class="text-xs text-blue-600 mb-2 font-bold">데이터를 불러오는 중입니다...</p>

          <ul class="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[600px] overflow-y-auto pr-2">
            <li v-for="(center, index) in centers" :key="index" class="bg-white p-5 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow relative">
              <span v-if="center.name === '서울본사'" class="absolute top-4 right-4 text-[10px] font-black bg-blue-100 text-blue-600 px-2 py-1 rounded">기본 본사</span>
              
              <h4 class="text-lg font-extrabold text-gray-800 mb-4">{{ center.name }}</h4>
              
              <div class="space-y-3 mb-5">
                <div class="bg-gray-50 p-3 rounded-lg border border-gray-100">
                  <p class="text-[10px] text-gray-500 font-bold mb-1">센터장 (영업소 관리비 수령)</p>
                  <p v-if="center.head" class="text-sm font-bold text-blue-700">{{ center.head.name }} <span class="text-xs text-gray-400 font-normal">| {{ center.head.account }}</span></p>
                  <p v-else class="text-sm font-bold text-gray-400">미지정</p>
                </div>
                <div class="bg-gray-50 p-3 rounded-lg border border-gray-100">
                  <p class="text-[10px] text-gray-500 font-bold mb-1">소개인 (영업소 소개비 수령)</p>
                  <p v-if="center.referrer" class="text-sm font-bold text-purple-700">{{ center.referrer.name }} <span class="text-xs text-gray-400 font-normal">| {{ center.referrer.account }}</span></p>
                  <p v-else class="text-sm font-bold text-gray-400">미지정</p>
                </div>
              </div>

              <div class="flex space-x-2 pt-3 border-t border-gray-100">
                <button @click="openCenterEditModal(center, index)" class="flex-1 bg-gray-100 text-gray-700 font-bold py-2 rounded-lg text-xs hover:bg-gray-200 transition-colors">상세 관리 및 수정</button>
                <button v-if="center.name !== '서울본사'" @click="removeCenter(index)" class="bg-white border border-red-200 text-red-500 font-bold py-2 px-4 rounded-lg text-xs hover:bg-red-50 transition-colors">삭제</button>
              </div>
            </li>
          </ul>
        </div>
        <div v-if="showCenterEditModal" class="fixed inset-0 z-[80] flex items-center justify-center bg-black bg-opacity-60 no-print px-4">
          <div class="bg-white rounded-2xl w-full max-w-lg p-6 shadow-2xl relative max-h-[90vh] overflow-y-auto">
            <button @click="closeCenterEditModal" class="absolute top-4 right-4 text-gray-400 hover:text-gray-800">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg>
            </button>
            <h3 class="text-xl font-extrabold text-gray-800 mb-4 border-b pb-3">센터 상세 관리</h3>
            
            <div class="space-y-6">
              <div>
                <label class="block text-xs font-bold text-gray-700 mb-2">센터(영업소) 이름</label>
                <input type="text" v-model="editingCenter.name" class="w-full px-4 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm font-bold focus:outline-none focus:ring-2 focus:ring-blue-500" />
                <p class="text-[10px] text-blue-500 mt-1 font-semibold">💡 센터명 변경 시, 기존에 해당 센터로 가입된 모든 회원들의 소속 정보도 자동으로 일괄 변경됩니다.</p>
              </div>

              <div class="bg-blue-50 p-4 rounded-xl border border-blue-100">
                <h4 class="text-sm font-bold text-blue-900 mb-3">👑 센터장 지정 (영업소 관리비 수령 대상)</h4>
                
                <div v-if="editingCenter.head" class="flex justify-between items-center bg-white p-3 rounded border border-blue-200">
                  <div>
                    <p class="font-bold text-gray-800 text-sm">{{ editingCenter.head.name }}</p>
                    <p class="text-xs text-gray-500 font-mono mt-0.5">계좌: {{ editingCenter.head.account }}</p>
                  </div>
                  <button @click="editingCenter.head = null" class="text-xs font-bold text-red-500 bg-red-50 px-3 py-1.5 rounded border border-red-100 hover:bg-red-100">해제</button>
                </div>
                
                <div v-else class="space-y-3">
                  <div class="flex space-x-2">
                    <input type="text" v-model="centerUserSearchQuery" placeholder="회원 이름 검색" class="flex-1 px-3 py-2 text-sm border rounded" />
                    <button @click="searchCenterUser('head')" class="bg-blue-600 text-white text-xs font-bold px-4 rounded hover:bg-blue-700">검색</button>
                  </div>
                  
                  <div v-if="centerUserSearchResult && centerUserTargetRole === 'head'" class="bg-white p-3 border border-green-200 rounded-lg shadow-sm">
                    <p class="text-sm font-bold text-gray-800 mb-2">검색된 회원: <span class="text-green-600">{{ centerUserSearchResult.name }}</span></p>
                    <label class="block text-[10px] font-bold text-gray-500 mb-1">수당 지급용 계좌번호 (필수)</label>
                    <div class="flex space-x-2">
                      <input type="text" v-model="centerUserAccount" placeholder="은행명 / 계좌번호 / 예금주" class="flex-1 px-3 py-2 text-xs border rounded focus:ring-green-500" />
                      <button @click="assignCenterUser" class="bg-green-600 text-white text-xs font-bold px-4 rounded hover:bg-green-700">지정 완료</button>
                    </div>
                  </div>
                </div>
              </div>

              <div class="bg-purple-50 p-4 rounded-xl border border-purple-100">
                <h4 class="text-sm font-bold text-purple-900 mb-3">🤝 소개인 지정 (영업소 소개비 수령 대상)</h4>
                
                <div v-if="editingCenter.referrer" class="flex justify-between items-center bg-white p-3 rounded border border-purple-200">
                  <div>
                    <p class="font-bold text-gray-800 text-sm">{{ editingCenter.referrer.name }}</p>
                    <p class="text-xs text-gray-500 font-mono mt-0.5">계좌: {{ editingCenter.referrer.account }}</p>
                  </div>
                  <button @click="editingCenter.referrer = null" class="text-xs font-bold text-red-500 bg-red-50 px-3 py-1.5 rounded border border-red-100 hover:bg-red-100">해제</button>
                </div>
                
                <div v-else class="space-y-3">
                  <div class="flex space-x-2">
                    <input type="text" v-model="centerUserSearchQuery" placeholder="회원 이름 검색" class="flex-1 px-3 py-2 text-sm border rounded" />
                    <button @click="searchCenterUser('referrer')" class="bg-purple-600 text-white text-xs font-bold px-4 rounded hover:bg-purple-700">검색</button>
                  </div>
                  
                  <div v-if="centerUserSearchResult && centerUserTargetRole === 'referrer'" class="bg-white p-3 border border-green-200 rounded-lg shadow-sm">
                    <p class="text-sm font-bold text-gray-800 mb-2">검색된 회원: <span class="text-green-600">{{ centerUserSearchResult.name }}</span></p>
                    <label class="block text-[10px] font-bold text-gray-500 mb-1">수당 지급용 계좌번호 (필수)</label>
                    <div class="flex space-x-2">
                      <input type="text" v-model="centerUserAccount" placeholder="은행명 / 계좌번호 / 예금주" class="flex-1 px-3 py-2 text-xs border rounded focus:ring-green-500" />
                      <button @click="assignCenterUser" class="bg-green-600 text-white text-xs font-bold px-4 rounded hover:bg-green-700">지정 완료</button>
                    </div>
                  </div>
                </div>
              </div>

              <div class="flex space-x-3 pt-4 border-t border-gray-200">
                <button @click="closeCenterEditModal" class="flex-1 bg-gray-200 text-gray-700 font-bold py-3.5 rounded-xl hover:bg-gray-300 transition-colors">취소</button>
                <button @click="saveCenterEdit" class="flex-1 bg-[#1e2532] text-white font-bold py-3.5 rounded-xl hover:bg-black shadow-md transition-colors">변경 내용 저장</button>
              </div>
            </div>
          </div>
        </div>

        <div v-else-if="currentMenu === '공지사항'" class="bg-white/95 backdrop-blur-sm p-6 rounded-xl shadow-2xl border border-gray-200 flex space-x-6">
          <div class="w-1/3 border-r pr-6">
            <h3 class="text-lg font-bold text-gray-800 mb-4">{{ editingNotice ? '공지사항 수정' : '새 공지사항 등록' }}</h3>
            <form @submit.prevent="saveNotice" class="space-y-4">
              <div>
                <label class="block text-xs font-bold text-gray-700 mb-1">제목</label>
                <input type="text" v-model="noticeForm.title" required class="w-full px-3 py-2 bg-gray-50 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="공지 제목" />
              </div>
              <div>
                <label class="block text-xs font-bold text-gray-700 mb-1">내용</label>
                <textarea v-model="noticeForm.content" required rows="6" class="w-full px-3 py-2 bg-gray-50 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="공지 내용 입력"></textarea>
              </div>
              <div class="flex items-center">
                <input type="checkbox" v-model="noticeForm.isActive" id="isActive" class="mr-2 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
                <label for="isActive" class="text-sm font-bold text-blue-800 cursor-pointer">회원 대시보드 메인에 노출 (1개만 적용됨)</label>
              </div>
              <div class="flex space-x-2 pt-2">
                <button type="button" v-if="editingNotice" @click="resetNoticeForm" class="flex-1 bg-gray-200 text-gray-700 font-bold py-2.5 rounded-lg text-sm hover:bg-gray-300 transition-colors">취소</button>
                <button type="submit" class="flex-1 bg-blue-600 text-white font-bold py-2.5 rounded-lg text-sm hover:bg-blue-700 transition-colors">{{ editingNotice ? '수정하기' : '등록하기' }}</button>
              </div>
            </form>
          </div>
          
          <div class="flex-1">
            <h3 class="text-lg font-bold text-gray-800 mb-4">공지사항 목록</h3>
            <ul class="space-y-3 max-h-[600px] overflow-y-auto pr-2">
              <li v-for="n in notices" :key="n.id" class="p-4 border rounded-xl bg-gray-50 flex justify-between items-start transition-all" :class="{'border-blue-400 bg-blue-50 shadow-sm': n.isActive}">
                <div>
                  <div class="flex items-center space-x-2 mb-1">
                    <span v-if="n.isActive" class="bg-blue-600 text-white text-[10px] font-bold px-2 py-0.5 rounded shadow-sm">메인 노출중</span>
                    <h4 class="font-bold text-gray-800 text-sm">{{ n.title }}</h4>
                  </div>
                  <p class="text-xs text-gray-600 whitespace-pre-wrap mt-2">{{ n.content }}</p>
                  <p class="text-[10px] text-gray-400 mt-3">{{ formatDate(n.createdAt) }}</p>
                </div>
                <div class="space-x-2 shrink-0 ml-4">
                  <button @click="editNotice(n)" class="text-xs text-blue-600 font-bold hover:underline bg-white px-2 py-1 rounded border border-blue-200">수정</button>
                  <button @click="deleteNotice(n.id)" class="text-xs text-red-500 font-bold hover:underline bg-white px-2 py-1 rounded border border-red-200">삭제</button>
                </div>
              </li>
              <li v-if="notices.length === 0" class="text-center py-10 text-sm text-gray-500 font-bold">등록된 공지사항이 없습니다.</li>
            </ul>
          </div>
        </div>

        <div v-else-if="currentMenu === '시스템설정'" class="space-y-6">
          <div class="bg-white/95 backdrop-blur-sm p-6 rounded-xl shadow-2xl border border-gray-200">
            <h3 class="text-lg font-bold text-gray-800 mb-4 border-b pb-2">회사 정책 설정</h3>
            <div class="flex items-center space-x-4">
              <label class="text-sm font-bold text-gray-700">기본 시스템/출금 수수료 (%)</label>
              <input type="number" v-model="systemFee" class="w-24 px-3 py-2 border border-gray-300 rounded-lg text-center font-bold focus:outline-none focus:ring-2 focus:ring-blue-500" />
              <button @click="saveSystemFee" class="bg-[#1e2532] text-white font-bold py-2 px-6 rounded-lg hover:bg-black text-sm transition-colors shadow-md">설정 저장</button>
            </div>
            <p class="text-xs text-gray-500 mt-3">* 설정된 수수료(%)는 회원의 보너스 발생 및 출금 시 시스템에서 일괄 차감 적용됩니다.</p>
          </div>

          <div class="bg-white/95 backdrop-blur-sm p-6 rounded-xl shadow-2xl border border-gray-200">
            <h3 class="text-lg font-bold text-gray-800 mb-4 border-b pb-2 text-red-600">회원 계정 정지 관리</h3>
            <div class="flex space-x-2 mb-4">
              <input type="text" v-model="suspendSearchQuery" @keyup.enter="searchSuspendUser" class="flex-1 px-4 py-2 bg-[#f0f4f8] border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="회원 이름 또는 UID 검색" />
              <button @click="searchSuspendUser" class="bg-gray-800 text-white font-bold py-2 px-6 rounded-lg text-sm hover:bg-gray-900 transition-colors shadow-md">검색</button>
            </div>
            
            <div v-if="suspendTargetUser" class="p-5 border rounded-xl bg-gray-50 flex items-center justify-between mt-4 shadow-sm">
              <div>
                <p class="font-bold text-gray-800 text-lg">{{ suspendTargetUser.name }} <span class="text-sm text-gray-500 font-normal">({{ suspendTargetUser.email }})</span></p>
                <p class="text-xs text-gray-500 font-mono mt-1">UID: {{ suspendTargetUser.uid }}</p>
                <p class="text-sm font-bold mt-3" :class="suspendTargetUser.isSuspended ? 'text-red-600' : 'text-green-600'">
                  현재 상태: {{ suspendTargetUser.isSuspended ? '정지됨 (로그인 차단)' : '정상 활동중' }}
                </p>
              </div>
              <button 
                @click="toggleSuspend(suspendTargetUser)" 
                class="font-bold py-2.5 px-5 rounded-lg text-sm transition-colors shadow-sm"
                :class="suspendTargetUser.isSuspended ? 'bg-green-100 text-green-700 hover:bg-green-200 border border-green-200' : 'bg-red-600 text-white hover:bg-red-700'"
              >
                {{ suspendTargetUser.isSuspended ? '정지 해제하기' : '계정 정지하기' }}
              </button>
            </div>
          </div>

          <div class="bg-white/95 backdrop-blur-sm p-6 rounded-xl shadow-2xl border border-gray-200">
            <h3 class="text-lg font-bold text-gray-800 mb-4 border-b pb-2 text-red-600 flex items-center">
              <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg>
              데이터 초기화 (주의)
            </h3>
            
            <div class="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <h4 class="text-sm font-bold text-gray-800 mb-2">특정 회원 초기화 (1명)</h4>
              <div class="flex space-x-2">
                <input type="text" v-model="resetTargetUid" class="flex-1 px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500" placeholder="초기화할 회원의 UID를 정확히 입력하세요" />
                <button @click="resetData('single')" :disabled="isResetting" class="bg-gray-800 text-white font-bold py-2 px-6 rounded-lg text-sm hover:bg-black transition-colors shadow-md disabled:opacity-50">
                  개별 초기화
                </button>
              </div>
              <p class="text-xs text-gray-500 mt-2">* 해당 회원의 PV, 포인트, 수당 그릇이 0으로 리셋되며, 관련 내역이 삭제됩니다.</p>
            </div>

            <div class="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center justify-between shadow-sm">
              <div>
                <h4 class="text-sm font-bold text-red-800 mb-1">전체 시스템 데이터 초기화 (테스트 종료 시)</h4>
                <p class="text-xs text-red-600">모든 회원의 실적을 0으로 만들고, 시스템의 모든 매출/수당/출금 내역을 영구 삭제합니다.</p>
              </div>
              <button @click="resetData('all')" :disabled="isResetting" class="bg-red-600 text-white font-bold py-2.5 px-6 rounded-lg text-sm hover:bg-red-700 transition-colors shadow-md shrink-0 disabled:opacity-50">
                전체 초기화 실행
              </button>
            </div>
          </div>
          </div>

        <!-- 조직도 관리 화면 (D3 네트워크) -->
        <div v-else-if="currentMenu === '조직도'" class="bg-white/95 backdrop-blur-sm p-6 rounded-xl shadow-2xl border border-gray-200 min-h-[700px] flex flex-col">
          <h3 class="text-lg font-bold text-gray-800 mb-4 border-b pb-2">회원 계보도 검색</h3>
          
          <div class="flex space-x-2 mb-4">
            <input type="text" v-model="treeSearchQuery" @keyup.enter="searchTreeUser" class="w-1/3 px-4 py-2 bg-[#f0f4f8] border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="회원 이름, 이메일 또는 UID 검색" />
            <button @click="searchTreeUser" class="bg-blue-600 text-white font-bold py-2 px-6 rounded-lg text-sm shadow-md hover:bg-blue-700 transition-colors">검색</button>
          </div>

          <div v-if="treeRootUser" class="flex-1 flex flex-col relative">
            <div class="flex p-1 bg-gray-200 rounded-xl shadow-inner mb-4">
              <button @click="changeTreeTab('sponsor')" :class="['flex-1 py-2 text-sm font-bold rounded-lg transition-all', adminTreeTab === 'sponsor' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700']">후원 네트워크 (바이너리)</button>
              <button @click="changeTreeTab('recommend')" :class="['flex-1 py-2 text-sm font-bold rounded-lg transition-all', adminTreeTab === 'recommend' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700']">추천 네트워크 (직추천)</button>
            </div>

            <div class="bg-gray-50 rounded-xl border border-gray-200 relative flex-1 overflow-hidden min-h-[500px]">
              <div v-if="isLoadingTree" class="absolute inset-0 bg-white/70 backdrop-blur-sm flex items-center justify-center z-10">
                <p class="text-sm font-bold text-blue-600 animate-pulse">네트워크 구성 중...</p>
              </div>
              <div class="flex items-center justify-between p-3 absolute top-0 left-0 right-0 z-10 pointer-events-none">
                <button @click="goUpTree" :disabled="viewHistory.length === 0" class="text-xs font-bold px-3 py-1.5 rounded-lg flex items-center transition-colors pointer-events-auto shadow-sm" :class="viewHistory.length > 0 ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-200 text-gray-400 cursor-not-allowed'">
                  <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path></svg>
                  상위 노드로
                </button>
                <span class="text-[10px] text-gray-500 font-medium">마우스 드래그 및 휠(줌) 가능</span>
              </div>
              <div ref="d3Container" class="w-full h-full cursor-move mt-10"></div>
            </div>
          </div>
        </div>

      </div>
    </main>

    <!-- [계보도 상세 정보 모달] -->
    <div v-if="showTreeUserModal" class="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-60 px-4 transition-opacity no-print">
      <div class="bg-white rounded-2xl w-full max-w-sm overflow-hidden shadow-2xl transform scale-100">
        <div class="bg-[#1e2532] p-4 flex justify-between items-center">
          <h3 class="text-white font-bold">회원 상세 정보</h3>
          <button @click="showTreeUserModal = false" class="text-gray-300 hover:text-white">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg>
          </button>
        </div>
        <div class="p-6">
          <div class="text-center mb-5">
            <span class="bg-blue-100 text-blue-700 text-xs font-black px-2 py-1 rounded">{{ selectedTreeUser.level }}</span>
            <h2 class="text-2xl font-extrabold text-gray-800 mt-2">{{ selectedTreeUser.name }}</h2>
            <p class="text-xs font-mono text-gray-400 mt-1">UID: {{ selectedTreeUser.uid }}</p>
          </div>
          <div class="bg-gray-50 rounded-xl p-4 border border-gray-100 space-y-3">
            <div class="flex justify-between"><span class="text-xs font-bold text-gray-500">가입일</span><span class="text-sm font-bold text-gray-800">{{ formatDate(selectedTreeUser.createdAt) }}</span></div>
            <div class="flex justify-between"><span class="text-xs font-bold text-gray-500">소속 센터</span><span class="text-sm font-bold text-gray-800">{{ selectedTreeUser.center }}</span></div>
            <div class="flex justify-between items-center border-t border-gray-200 pt-3"><span class="text-xs font-bold text-blue-600">본인 누적 PV</span><span class="text-lg font-black text-blue-600">{{ formatNumber(selectedTreeUser.personalPV || 0) }} PV</span></div>
          </div>
          <div class="grid grid-cols-2 gap-2 mt-3 text-center">
            <div class="bg-red-50 p-2 rounded-lg border border-red-100"><p class="text-[10px] text-red-800 font-bold mb-1">좌측 대기 PV</p><p class="text-sm font-extrabold text-red-600">{{ formatNumber(selectedTreeUser.cycleLeftPV || 0) }}</p></div>
            <div class="bg-blue-50 p-2 rounded-lg border border-blue-100"><p class="text-[10px] text-blue-800 font-bold mb-1">우측 대기 PV</p><p class="text-sm font-extrabold text-blue-600">{{ formatNumber(selectedTreeUser.cycleRightPV || 0) }}</p></div>
          </div>
          <button @click="drillDownTree(selectedTreeUser)" class="w-full mt-5 bg-gray-800 text-white font-bold py-3 rounded-xl hover:bg-black transition-colors shadow-md text-sm">이 회원을 기준으로 네트워크 보기 ➔</button>
        </div>
      </div>
    </div>

    <!-- [회원 정보 수정 모달] -->
    <div v-if="showUserEditModal" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 no-print">
      <div class="bg-white rounded-xl w-full max-w-md p-6 shadow-2xl relative">
        <button @click="closeUserEditModal" class="absolute top-4 right-4 text-gray-400 hover:text-gray-800">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg>
        </button>
        <h3 class="text-lg font-bold text-gray-800 mb-4 border-b pb-2">회원 정보 수정</h3>
        
        <form @submit.prevent="saveUserEdit" class="space-y-4">
          <div>
            <label class="block text-xs font-bold text-gray-700 mb-1">이름</label>
            <input type="text" v-model="editUserData.name" class="w-full px-3 py-2 border rounded text-sm focus:outline-none focus:border-blue-500" />
          </div>
          <div>
            <label class="block text-xs font-bold text-gray-700 mb-1">연락처</label>
            <input type="text" v-model="editUserData.phone" class="w-full px-3 py-2 border rounded text-sm focus:outline-none focus:border-blue-500" />
          </div>
          <div class="flex space-x-2">
            <div class="flex-1">
              <label class="block text-xs font-bold text-gray-700 mb-1">패키지 (직급)</label>
              <select v-model="editUserData.level" class="w-full px-3 py-2 border rounded text-sm focus:outline-none focus:border-blue-500">
                <option value="1L">1L</option>
                <option value="2L">2L</option>
                <option value="3L">3L</option>
                <option value="4L">4L</option>
                <option value="5L">5L</option>
                <option value="VIP">VIP</option>
              </select>
            </div>
            <div class="flex-1">
              <label class="block text-xs font-bold text-gray-700 mb-1">센터</label>
              <select v-model="editUserData.center" class="w-full px-3 py-2 border rounded text-sm focus:outline-none focus:border-blue-500">
                <option v-for="c in centers" :key="c.name" :value="c.name">{{ c.name }}</option>
              </select>
            </div>
          </div>
          <div class="flex space-x-2">
            <div class="flex-1">
              <label class="block text-xs font-bold text-blue-700 mb-1">본인 누적 PV (보정)</label>
              <input type="number" v-model.number="editUserData.personalPV" class="w-full px-3 py-2 bg-blue-50 border border-blue-200 rounded text-sm font-bold text-blue-800 focus:outline-none focus:border-blue-500" />
            </div>
            <div class="flex-1">
              <label class="block text-xs font-bold text-green-700 mb-1">출금 가능 잔액 (보정)</label>
              <input type="number" v-model.number="editUserData.availableBalance" class="w-full px-3 py-2 bg-green-50 border border-green-200 rounded text-sm font-bold text-green-800 focus:outline-none focus:border-green-500" />
            </div>
          </div>
          <div class="flex space-x-2">
            <!-- 추천인 검색 및 수정 -->
            <div class="flex-1 p-3 bg-gray-50 border border-gray-200 rounded-lg">
              <label class="block text-xs font-bold text-gray-700 mb-2">추천인 변경</label>
              <div class="flex space-x-1 mb-2">
                <input type="text" v-model="relSearchQuery.recommender" @keyup.enter.prevent="searchRelationUser('recommender')" placeholder="이름 검색" class="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:border-blue-500" />
                <button type="button" @click="searchRelationUser('recommender')" class="bg-gray-800 text-white px-3 py-1.5 rounded text-xs font-bold hover:bg-black shrink-0">검색</button>
              </div>
              <input type="text" v-model="editUserData.recommender" class="w-full px-3 py-2 border border-gray-300 rounded text-xs font-mono focus:outline-none focus:border-blue-500 bg-white" placeholder="UID 직접 입력가능" />
              <p v-if="relSearchResult.recommender" class="text-[10px] text-blue-600 font-bold mt-1 truncate">✓ 검색됨: {{ relSearchResult.recommender }}</p>
            </div>
            <!-- 후원인 검색 및 수정 -->
            <div class="flex-1 p-3 bg-gray-50 border border-gray-200 rounded-lg">
              <label class="block text-xs font-bold text-gray-700 mb-2">후원인 변경</label>
              <div class="flex space-x-1 mb-2">
                <input type="text" v-model="relSearchQuery.supporter" @keyup.enter.prevent="searchRelationUser('supporter')" placeholder="이름 검색" class="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:border-blue-500" />
                <button type="button" @click="searchRelationUser('supporter')" class="bg-gray-800 text-white px-3 py-1.5 rounded text-xs font-bold hover:bg-black shrink-0">검색</button>
              </div>
              <input type="text" v-model="editUserData.supporter" class="w-full px-3 py-2 border border-gray-300 rounded text-xs font-mono focus:outline-none focus:border-blue-500 bg-white" placeholder="UID 직접 입력가능" />
              <p v-if="relSearchResult.supporter" class="text-[10px] text-blue-600 font-bold mt-1 truncate">✓ 검색됨: {{ relSearchResult.supporter }}</p>
            </div>
          </div>
          <div class="pt-4 flex space-x-2">
            <button type="button" @click="closeUserEditModal" class="flex-1 bg-gray-200 text-gray-700 font-bold py-2 rounded shadow-sm hover:bg-gray-300">취소</button>
            <button type="submit" class="flex-1 bg-blue-600 text-white font-bold py-2 rounded shadow-sm hover:bg-blue-700">정보 저장</button>
          </div>
        </form>
      </div>
    </div>

  </div>
<div v-if="showBonusModal" class="fixed inset-0 z-[70] flex items-center justify-center bg-black bg-opacity-60 no-print">
      <div class="bg-white rounded-xl w-full max-w-md p-6 shadow-2xl relative">
        <button @click="closeBonusModal" class="absolute top-4 right-4 text-gray-400 hover:text-gray-800">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg>
        </button>
        <h3 class="text-lg font-bold text-gray-800 mb-4 border-b pb-2 flex items-center">
          <svg class="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
          수당 상세 내역 및 보정
        </h3>
        
        <div class="space-y-3 mb-6 bg-gray-50 p-4 rounded-lg border border-gray-200">
          <div class="flex justify-between">
            <span class="text-xs font-bold text-gray-500">수령 회원</span>
            <span class="text-sm font-bold text-gray-800">{{ selectedBonus.receiverName }} <span class="text-[10px] text-gray-400 font-mono">({{ selectedBonus.uid }})</span></span>
          </div>
          <div class="flex justify-between">
            <span class="text-xs font-bold text-gray-500">발생 원인 회원</span>
            <span class="text-sm font-bold text-blue-600">{{ selectedBonus.sourceName }} <span class="text-[10px] text-blue-400 font-mono">({{ selectedBonus.sourceUid || '시스템' }})</span></span>
          </div>
          <div class="flex justify-between">
            <span class="text-xs font-bold text-gray-500">보너스 종류 / 내용</span>
            <span class="text-xs font-bold text-gray-800">{{ selectedBonus.bonusType }} ({{ selectedBonus.description }})</span>
          </div>
          <div class="flex justify-between">
            <span class="text-xs font-bold text-gray-500">발생 일시</span>
            <span class="text-xs font-bold text-gray-800">{{ formatDate(selectedBonus.createdAt) }}</span>
          </div>
        </div>

        <form @submit.prevent="saveBonusEdit">
          <label class="block text-xs font-bold text-gray-700 mb-2">지급액 수동 보정</label>
          <div class="relative">
            <input type="number" v-model="editBonusAmount" class="w-full px-4 py-3 bg-green-50 border border-green-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-lg font-black text-green-700 text-right pr-12 transition-all" />
            <span class="absolute right-4 top-3.5 text-green-600 font-bold">원</span>
          </div>
          <p class="text-[11px] text-red-500 mt-2 font-semibold">
            * 주의: 저장 시 기존 내역이 수정되며, 해당 회원의 지갑 잔액도 차액만큼 즉시 자동 증감 처리됩니다.
          </p>

          <div class="pt-5 flex space-x-2">
            <button type="button" @click="closeBonusModal" class="flex-1 bg-gray-200 text-gray-700 font-bold py-3 rounded-lg shadow-sm hover:bg-gray-300">취소</button>
            <button type="submit" class="flex-1 bg-green-600 text-white font-bold py-3 rounded-lg shadow-sm hover:bg-green-700">금액 수정 및 잔액 보정</button>
          </div>
        </form>
      </div>
    </div>

</template>

<script>
import { ref, onMounted, onBeforeUnmount, watch, computed, shallowRef, markRaw } from 'vue';
import { auth, db } from '../../firebase'; // ⭐️ auth, 를 추가해주세요!
import { doc, getDoc, setDoc, collection, query, where, getDocs, updateDoc, addDoc, serverTimestamp, deleteDoc, increment } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import * as d3 from 'd3';

export default {
  name: 'AdminDashboard',
  setup() {
    const canvasRef = ref(null);
    let ctx = null;
    let animationFrameId = null;
    const particles = [];
    const particleCount = 100;

    const menuList = ['입금대기', '승인완료내역', '회원관리', '센터관리', '출금관리', '수당관리', '공지사항', '시스템설정', '조직도'];
    const currentMenu = ref('입금대기');
    
    const rawData = ref([]);
    const isLoading = ref(false);

    const searchQuery = ref('');
    const searchCenter = ref('');
    const searchDate = ref('');
    const searchStatus = ref('');
    const sortKey = ref('createdAtTime');
    const sortOrder = ref('desc');

    const centers = ref([]);
    const newCenterName = ref('');

    const notices = ref([]);
    const editingNotice = ref(false);
    const noticeForm = ref({ title: '', content: '', isActive: false });
    let currentEditId = null;

    const systemFee = ref(5);
    const suspendSearchQuery = ref('');
    const suspendTargetUser = ref(null);

    const treeSearchQuery = ref('');
    const treeRootUser = ref(null);
    const adminTreeTab = ref('sponsor');
    
    const isLoadingTree = ref(false);
    const viewHistory = ref([]);
    const d3Container = ref(null);
    const graphData = shallowRef({ nodes: [], links: [] });
    const showTreeUserModal = ref(false);
    const selectedTreeUser = ref({});

    const showUserEditModal = ref(false);
    const editUserData = ref({});

    const relSearchQuery = ref({ recommender: '', supporter: '' });
    const relSearchResult = ref({ recommender: '', supporter: '' });

    const searchRelationUser = async (type) => {
      const name = relSearchQuery.value[type].trim();
      if (!name) return;

      const q = query(collection(db, 'users'), where('name', '==', name));
      const snap = await getDocs(q);

      if (!snap.empty) {
        const user = snap.docs[0].data();
        editUserData.value[type] = user.uid;
        relSearchResult.value[type] = `${user.name} (${user.email})`;
      } else {
        alert('해당 이름의 회원을 찾을 수 없습니다.');
        relSearchResult.value[type] = '';
      }
    };

    watch(currentMenu, (newVal) => {
      resetSearch();
      sortKey.value = newVal === '승인완료내역' ? 'approvedAtTime' : 'createdAtTime';
      sortOrder.value = 'desc';

      if (['입금대기', '승인완료내역', '회원관리', '출금관리', '수당관리'].includes(newVal)) {
        refreshData();
      } else if (newVal === '센터관리') {
        loadCenters();
      } else if (newVal === '공지사항') {
        loadNotices();
      } else if (newVal === '시스템설정') {
        loadSystemSettings();
      }
    });

    const refreshData = async () => {
      isLoading.value = true;
      rawData.value = [];
      try {
        if (currentMenu.value === '입금대기') {
          await loadPurchaseRequests('PENDING');
        } else if (currentMenu.value === '승인완료내역') {
          await loadPurchaseRequests('APPROVED');
        } else if (currentMenu.value === '회원관리') {
          await loadUsers();
        } else if (currentMenu.value === '출금관리') {
          await loadWithdrawals();
        } else if (currentMenu.value === '수당관리') {
          await loadBonuses();
        }
      } catch (e) {
        console.error('Data load error:', e);
      } finally {
        isLoading.value = false;
      }
    };

    const showCenterEditModal = ref(false);
    const editingCenter = ref(null);
    const editingCenterIndex = ref(-1);

    const centerUserSearchQuery = ref('');
    const centerUserSearchResult = ref(null);
    const centerUserTargetRole = ref(''); 
    const centerUserAccount = ref('');

    const openCenterEditModal = (center, index) => {
      editingCenter.value = JSON.parse(JSON.stringify(center));
      editingCenterIndex.value = index;
      showCenterEditModal.value = true;
      centerUserSearchQuery.value = '';
      centerUserSearchResult.value = null;
    };

    const closeCenterEditModal = () => {
      showCenterEditModal.value = false;
      editingCenter.value = null;
      editingCenterIndex.value = -1;
    };

    const searchCenterUser = async (role) => {
      centerUserTargetRole.value = role;
      centerUserSearchResult.value = null;
      centerUserAccount.value = '';
      if (!centerUserSearchQuery.value.trim()) return;

      const q = query(collection(db, 'users'), where('name', '==', centerUserSearchQuery.value.trim()));
      const snap = await getDocs(q);
      if (!snap.empty) {
        const userData = snap.docs[0].data();
        centerUserSearchResult.value = {
          uid: userData.uid,
          name: userData.name,
        };
        centerUserAccount.value = userData.account || ''; 
      } else {
        alert('해당 이름의 회원을 찾을 수 없습니다.');
      }
    };

    const assignCenterUser = () => {
      if (!centerUserAccount.value.trim()) {
        alert('지급을 위한 계좌번호를 반드시 입력해주세요.');
        return;
      }
      
      const targetData = {
        uid: centerUserSearchResult.value.uid,
        name: centerUserSearchResult.value.name,
        account: centerUserAccount.value.trim()
      };

      if (centerUserTargetRole.value === 'head') {
        editingCenter.value.head = targetData;
      } else {
        editingCenter.value.referrer = targetData;
      }
      
      centerUserSearchQuery.value = '';
      centerUserSearchResult.value = null;
      centerUserAccount.value = '';
    };

    const saveCenterEdit = async () => {
      if (!editingCenter.value.name.trim()) {
        alert('센터명을 입력해주세요.');
        return;
      }

      const oldCenterName = centers.value[editingCenterIndex.value].name;
      const newCenterName = editingCenter.value.name.trim();

      if (oldCenterName !== newCenterName) {
        if (!confirm(`센터명이 '${oldCenterName}'에서 '${newCenterName}'(으)로 변경됩니다.\n해당 센터에 소속된 기존 회원들의 정보도 모두 자동으로 업데이트 하시겠습니까?`)) {
          return;
        }

        try {
          const q = query(collection(db, 'users'), where('center', '==', oldCenterName));
          const snap = await getDocs(q);
          
          const updatePromises = [];
          snap.forEach(document => {
            updatePromises.push(updateDoc(doc(db, 'users', document.id), { center: newCenterName }));
          });
          
          if (updatePromises.length > 0) {
            await Promise.all(updatePromises);
            alert(`소속 회원 ${snap.size}명의 센터 정보가 자동으로 싹 업데이트 되었습니다!`);
          }
        } catch (error) {
          console.error('회원 센터 일괄 업데이트 에러:', error);
          alert('회원 정보 자동 업데이트 중 오류가 발생했습니다.');
        }
      }

      centers.value[editingCenterIndex.value] = editingCenter.value;
      await saveCenters();
      alert('센터 상세 정보가 성공적으로 저장되었습니다.');
      closeCenterEditModal();
      
      if (currentMenu.value === '회원관리') refreshData();
    };

    const loadPurchaseRequests = async (status) => {
      const q = query(collection(db, 'purchase_requests'), where('status', '==', status));
      const snap = await getDocs(q);
      const reqs = [];
      for (const d of snap.docs) {
        let data = { id: d.id, ...d.data() };
        const uSnap = await getDoc(doc(db, 'users', data.uid));
        if (uSnap.exists()) {
          const u = uSnap.data();
          data.name = u.name; data.phone = u.phone; data.center = u.center;
        } else {
          data.name = '알수없음'; data.phone = '-'; data.center = '-';
        }
        data.createdAtTime = data.createdAt?.toMillis() || 0;
        data.approvedAtTime = data.approvedAt?.toMillis() || 0;
        reqs.push(data);
      }
      rawData.value = reqs;
    };

    const loadUsers = async () => {
      const q = query(collection(db, 'users'), where('role', '==', 'member'));
      const snap = await getDocs(q);
      const users = [];
      const userMap = {}; 

      snap.forEach(d => {
        let data = d.data();
        data.createdAtTime = data.createdAt?.toMillis() || 0;
        
        const w = data.wallet || {};
        const totalBonus = (w.판매 || 0) + (w.매칭 || 0) + (w.대수 || 0) + (w.싸이클 || 0) + (w.스타 || 0) + (w.영업소관리 || 0) + (w.영업소소개 || 0) + (w.수동보정 || 0);
        data.availableBalance = totalBonus + (w.출금요청 || 0);

        users.push(data);
        userMap[data.uid] = data.name; 
      });

      users.forEach(u => {
        u.recommenderName = userMap[u.recommender] || (u.recommender === 'MASTER' ? '최상단(마스터)' : '');
        u.supporterName = userMap[u.supporter] || (u.supporter === 'MASTER' ? '최상단(마스터)' : '');
      });

      rawData.value = users;
    };

    const loadWithdrawals = async () => {
      const snap = await getDocs(collection(db, 'withdrawal_requests'));
      const reqs = [];
      const userCache = {}; 

      const getUser = async (uid) => {
        if (!uid) return null;
        if (userCache[uid]) return userCache[uid];
        const uSnap = await getDoc(doc(db, 'users', uid));
        if (uSnap.exists()) {
           userCache[uid] = uSnap.data();
           return userCache[uid];
        }
        return null;
      };

      for (const d of snap.docs) {
        let data = { id: d.id, ...d.data() };
        data.createdAtTime = data.createdAt?.toMillis() || 0;
        
        const reqUser = await getUser(data.uid);
        if (reqUser) {
          data.name = reqUser.name;
          data.email = reqUser.email;
          data.phone = reqUser.phone;
          const recUser = await getUser(reqUser.recommender);
          data.recommenderName = recUser ? recUser.name : (reqUser.recommender === 'MASTER' ? '마스터' : '-');
        } else {
          data.name = '알수없음'; data.email = '-'; data.phone = '-'; data.recommenderName = '-';
        }
        reqs.push(data);
      }
      rawData.value = reqs;
    };

    const loadBonuses = async () => {
      const snap = await getDocs(collection(db, 'bonus_history'));
      const reqs = [];
      const userCache = {}; 

      const getUser = async (uid) => {
        if (!uid) return null;
        if (userCache[uid]) return userCache[uid];
        const uSnap = await getDoc(doc(db, 'users', uid));
        if (uSnap.exists()) {
           userCache[uid] = uSnap.data();
           return userCache[uid];
        }
        return null;
      };

      for (const d of snap.docs) {
        let data = { id: d.id, ...d.data() };
        data.createdAtTime = data.createdAt?.toMillis() || 0;

        const receiver = await getUser(data.uid);
        if (receiver) {
           data.receiverName = receiver.name;
           data.receiverEmail = receiver.email;
           data.receiverPhone = receiver.phone;
           const recommender = await getUser(receiver.recommender);
           data.recommenderName = recommender ? recommender.name : (receiver.recommender === 'MASTER' ? '마스터' : '-');
        } else {
           data.receiverName = '알수없음';
           data.receiverEmail = '-'; data.receiverPhone = '-'; data.recommenderName = '-';
        }

        const sourceUser = await getUser(data.sourceUid);
        data.sourceName = sourceUser ? sourceUser.name : '시스템/알수없음';

        reqs.push(data);
      }
      rawData.value = reqs;
    };

    const showBonusModal = ref(false);
    const selectedBonus = ref({});
    const editBonusAmount = ref(0);

    const openBonusModal = (item) => {
      selectedBonus.value = { ...item };
      editBonusAmount.value = item.amount;
      showBonusModal.value = true;
    };

    const closeBonusModal = () => {
      showBonusModal.value = false;
      selectedBonus.value = {};
      editBonusAmount.value = 0;
    };

    const saveBonusEdit = async () => {
      if (!confirm('수당 금액을 수동으로 변경하시겠습니까?\n해당 회원의 지갑 잔액도 함께 보정됩니다.')) return;
      try {
        const newAmount = Number(editBonusAmount.value);
        const oldAmount = Number(selectedBonus.value.amount);
        const diff = newAmount - oldAmount; 

        if (diff === 0) { alert('변경된 금액이 없습니다.'); return; }

        const bonusRef = doc(db, 'bonus_history', selectedBonus.value.id);
        const userRef = doc(db, 'users', selectedBonus.value.uid);

        await updateDoc(bonusRef, {
          amount: newAmount,
          isManuallyEdited: true, 
          editedAt: serverTimestamp()
        });

        const type = selectedBonus.value.bonusType;
        await updateDoc(userRef, {
          totalEarnedBonus: increment(diff),
          [`wallet.${type}`]: increment(diff)
        });

        alert('성공적으로 수정 및 지갑 잔액이 보정되었습니다.');
        closeBonusModal();
        refreshData();
      } catch (error) {
        console.error("수당 수정 에러:", error);
        alert('수정 중 오류가 발생했습니다.');
      }
    };

    const filteredData = computed(() => {
      let result = rawData.value.slice(); 
      
      if (searchQuery.value) {
        const kw = searchQuery.value.toLowerCase();
        result = result.filter(r => 
          (r.name && r.name.toLowerCase().includes(kw)) || 
          (r.email && r.email.toLowerCase().includes(kw)) ||
          (r.uid && r.uid.toLowerCase().includes(kw))
        );
      }
      if (searchCenter.value && ['입금대기', '승인완료내역', '회원관리'].includes(currentMenu.value)) {
        result = result.filter(r => r.center === searchCenter.value);
      }
      if (searchDate.value) {
        result = result.filter(r => {
          const dateField = (currentMenu.value === '승인완료내역' && r.approvedAt) ? r.approvedAt : r.createdAt;
          const dStr = formatDate(dateField).substring(0, 8);
          const sStr = searchDate.value.substring(2).replace(/-/g, '-');
          return dStr === sStr;
        });
      }
      
      if (searchStatus.value && currentMenu.value === '출금관리') {
        result = result.filter(r => r.status === searchStatus.value);
      }

      result.sort((a, b) => {
        let valA = a[sortKey.value];
        let valB = b[sortKey.value];
        if (valA === undefined) valA = '';
        if (valB === undefined) valB = '';
        if (valA < valB) return sortOrder.value === 'asc' ? -1 : 1;
        if (valA > valB) return sortOrder.value === 'asc' ? 1 : -1;
        return 0;
      });

      return result;
    });

    const sortBy = (key) => {
      if (sortKey.value === key) {
        sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc';
      } else {
        sortKey.value = key;
        sortOrder.value = 'desc';
      }
    };

    const resetSearch = () => {
      searchQuery.value = ''; searchCenter.value = ''; searchDate.value = ''; searchStatus.value = ''; 
    };

    const approveRequest = async (req) => {
      if (!confirm(`[${req.email||req.name}]님의 입금 확인을 완료하시겠습니까?\n승인 시 즉시 보너스가 지급됩니다.`)) return;
      try {
        const qty = req.quantity || 1;
        for (let i = 0; i < qty; i++) {
          await addDoc(collection(db, 'purchases'), { 
            uid: req.uid, 
            level: req.level, 
            center: req.center, 
            approvedAt: serverTimestamp() 
          });
        }
        await updateDoc(doc(db, 'purchase_requests', req.id), { status: 'APPROVED', approvedAt: serverTimestamp() });
        alert('승인되었습니다.');
        refreshData();
      } catch (e) { alert('오류 발생'); }
    };

    const rejectRequest = async (id) => {
      if (!confirm('이 요청을 거절(삭제) 하시겠습니까?')) return;
      await updateDoc(doc(db, 'purchase_requests', id), { status: 'REJECTED', rejectedAt: serverTimestamp() });
      refreshData();
    };

    const completeWithdrawal = async (id) => {
      if (!confirm('출금 완료 처리하시겠습니까?')) return;
      await updateDoc(doc(db, 'withdrawal_requests', id), { status: 'COMPLETED', completedAt: serverTimestamp() });
      refreshData();
    };

    const holdWithdrawal = async (id) => {
      if (!confirm('해당 출금 신청을 보류 처리하시겠습니까?')) return;
      await updateDoc(doc(db, 'withdrawal_requests', id), { status: 'HOLD', updatedAt: serverTimestamp() });
      refreshData();
    };

    const refundWithdrawal = async (item, actionName, statusName, isHardDelete = false) => {
      if (!confirm(`출금을 ${actionName}하시겠습니까?\n차감되었던 신청 금액(${formatNumber(item.amount)}원)이 회원의 지갑으로 반환됩니다.`)) return;
      try {
        if (isHardDelete) {
          await deleteDoc(doc(db, 'withdrawal_requests', item.id));
        } else {
          await updateDoc(doc(db, 'withdrawal_requests', item.id), { status: statusName, updatedAt: serverTimestamp() });
        }
        
        await updateDoc(doc(db, 'users', item.uid), {
          'wallet.출금요청': increment(item.amount)
        });
        
        alert(`${actionName} 처리 및 반환이 완료되었습니다.`);
        refreshData();
      } catch(e) {
        alert('처리 중 오류가 발생했습니다.');
      }
    };

    const openUserEditModal = (user) => {
      editUserData.value = { ...user };
      
      relSearchQuery.value = { recommender: '', supporter: '' };
      relSearchResult.value = { recommender: '', supporter: '' };
      
      showUserEditModal.value = true;
    };

    const closeUserEditModal = () => {
      showUserEditModal.value = false;
      editUserData.value = {};
    };

    const saveUserEdit = async () => {
      if (!confirm('회원 정보를 수정하시겠습니까?')) return;
      try {
        const userRef = doc(db, 'users', editUserData.value.uid);
        const updateData = {
          name: editUserData.value.name,
          phone: editUserData.value.phone,
          level: editUserData.value.level,
          center: editUserData.value.center,
          personalPV: Number(editUserData.value.personalPV) || 0,
          recommender: editUserData.value.recommender,
          supporter: editUserData.value.supporter
        };

        const originalBalance = rawData.value.find(u => u.uid === editUserData.value.uid)?.availableBalance || 0;
        const newBalance = Number(editUserData.value.availableBalance) || 0;
        const diff = newBalance - originalBalance;

        if (diff !== 0) {
          updateData['wallet.수동보정'] = increment(diff);
        }

        await updateDoc(userRef, updateData);
        alert('성공적으로 수정되었습니다.');
        showUserEditModal.value = false;
        refreshData(); 
      } catch (error) {
        console.error('회원 수정 실패:', error);
        alert('회원 정보 수정 중 오류가 발생했습니다.');
      }
    };

    const formatDate = (ts) => {
      if (!ts) return '-';
      const d = ts.toDate ? ts.toDate() : new Date(ts);
      return `${d.getFullYear().toString().slice(2)}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')} ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
    };

    const formatNumber = (num) => new Intl.NumberFormat('ko-KR').format(num || 0);

    const exportExcel = () => {
      let csv = "data:text/csv;charset=utf-8,\uFEFF"; 
      
      if (currentMenu.value === '입금대기' || currentMenu.value === '승인완료내역') {
        csv += "일시,이름,이메일,연락처,센터,패키지,금액\n";
        filteredData.value.forEach(r => csv += `${formatDate(currentMenu.value === '입금대기' ? r.createdAt : r.approvedAt)},${r.name},${r.email},${r.phone},${r.center},${r.level},${r.totalPrice||r.price}\n`);
      } else if (currentMenu.value === '회원관리') {
        csv += "가입일,이름,UID,이메일,연락처,추천인,후원인,센터,직급,본인PV,누적수당,싸이클\n";
        filteredData.value.forEach(r => csv += `${formatDate(r.createdAt)},${r.name},${r.uid},${r.email},${r.phone},${r.recommender},${r.supporter},${r.center},${r.level},${r.personalPV},${r.totalEarnedBonus},${r.cycleCount||0}\n`);
      } else if (currentMenu.value === '출금관리') {
        csv += "신청일,회원UID,계좌정보,신청금액,상태\n";
        filteredData.value.forEach(r => csv += `${formatDate(r.createdAt)},${r.uid},"${r.accountInfo}",${r.amount},${r.status}\n`);
      } else if (currentMenu.value === '수당관리') {
        csv += "발생일,수령회원UID,종류,내용,지급액,상태\n";
        filteredData.value.forEach(r => csv += `${formatDate(r.createdAt)},${r.uid},${r.bonusType},"${r.description}",${r.amount},${r.status}\n`);
      }

      const encodedUri = encodeURI(csv);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", `${currentMenu.value}_목록.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };

    const printPage = () => window.print();

    const loadCenters = async () => {
      isLoading.value = true;
      const docRef = doc(db, 'settings', 'center_info');
      const snap = await getDoc(docRef);
      if (snap.exists()) {
        const list = snap.data().list || [];
        centers.value = list.map(c => typeof c === 'string' ? { name: c, head: null, referrer: null } : c);
      } else {
        centers.value = [{ name: '서울본사', head: null, referrer: null }];
      }
      isLoading.value = false;
    };

    const saveCenters = async () => {
      try {
        const docRef = doc(db, 'settings', 'center_info');
        await setDoc(docRef, { list: centers.value });
      } catch (error) {
        console.error("센터 저장 실패:", error);
      }
    };

    const addCenter = async () => {
      const name = newCenterName.value.trim();
      if (name && !centers.value.some(c => c.name === name)) {
        centers.value.push({ name: name, head: null, referrer: null });
        newCenterName.value = '';
        await saveCenters();
      } else if (centers.value.some(c => c.name === name)) {
        alert('이미 등록된 센터입니다.');
      }
    };

    const removeCenter = async (index) => {
      if (confirm('해당 센터를 삭제하시겠습니까?')) {
        centers.value.splice(index, 1);
        await saveCenters();
      }
    };

    const loadNotices = async () => {
      const snap = await getDocs(collection(db, 'notices'));
      const list = [];
      snap.forEach(d => list.push({ id: d.id, ...d.data() }));
      notices.value = list.sort((a, b) => (b.createdAt?.toMillis() || 0) - (a.createdAt?.toMillis() || 0));
    };

    const saveNotice = async () => {
      if (noticeForm.value.isActive) {
        for (let n of notices.value) {
          if (n.isActive && n.id !== currentEditId) {
            await updateDoc(doc(db, 'notices', n.id), { isActive: false });
          }
        }
      }
      
      const payload = { 
        title: noticeForm.value.title, 
        content: noticeForm.value.content, 
        isActive: noticeForm.value.isActive 
      };

      if (editingNotice.value) {
        await updateDoc(doc(db, 'notices', currentEditId), payload);
        alert('수정되었습니다.');
      } else {
        payload.createdAt = serverTimestamp();
        await addDoc(collection(db, 'notices'), payload);
        alert('등록되었습니다.');
      }
      resetNoticeForm();
      loadNotices();
    };

    const editNotice = (n) => {
      editingNotice.value = true;
      currentEditId = n.id;
      noticeForm.value = { title: n.title, content: n.content, isActive: n.isActive || false };
    };

    const deleteNotice = async (id) => {
      if (confirm('삭제하시겠습니까?')) {
        await deleteDoc(doc(db, 'notices', id));
        loadNotices();
      }
    };

    const resetNoticeForm = () => {
      editingNotice.value = false;
      currentEditId = null;
      noticeForm.value = { title: '', content: '', isActive: false };
    };

    const loadSystemSettings = async () => {
      const snap = await getDoc(doc(db, 'settings', 'system_info'));
      if (snap.exists()) systemFee.value = snap.data().fee || 5;
    };

    const saveSystemFee = async () => {
      await setDoc(doc(db, 'settings', 'system_info'), { fee: systemFee.value }, { merge: true });
      alert('수수료가 설정되었습니다.');
    };

// === 회원 계정 정지(검색 및 상태 토글) 로직 ===
    const searchSuspendUser = async () => {
      suspendTargetUser.value = null;
      const queryText = suspendSearchQuery.value.trim();
      if (!queryText) return;

      try {
        const usersRef = collection(db, 'users');
        // 1. 이름으로 먼저 검색
        let q = query(usersRef, where('name', '==', queryText));
        let snap = await getDocs(q);

        // 2. 없으면 UID로 검색
        if (snap.empty) {
          q = query(usersRef, where('uid', '==', queryText));
          snap = await getDocs(q);
        }

        if (!snap.empty) {
          const uData = snap.docs[0].data();
          suspendTargetUser.value = {
            uid: uData.uid,
            name: uData.name,
            email: uData.email,
            // ⭐️ [수정] DB의 isActive가 false이면, 화면의 isSuspended는 true가 되도록 매핑
            isSuspended: uData.isActive === false 
          };
        } else {
          alert('해당 이름 또는 UID를 가진 회원을 찾을 수 없습니다.');
        }
      } catch (error) {
        console.error("회원 검색 에러:", error);
        alert('검색 중 오류가 발생했습니다.');
      }
    };

    const toggleSuspend = async (user) => {
      // ⭐️ [수정] 정지 상태(isSuspended)가 true면 새 상태(newIsActive)는 true(활성화), 반대면 false(정지)
      const newIsActive = user.isSuspended; 
      const confirmMsg = newIsActive 
        ? `[${user.name}] 회원의 계정 정지를 해제(활성화) 하시겠습니까?`
        : `[${user.name}] 회원의 계정을 즉시 정지하시겠습니까?\n정지 시 회원은 서비스 이용이 제한됩니다.`;
        
      if (!confirm(confirmMsg)) return;

      try {
        const userRef = doc(db, 'users', user.uid);
        await updateDoc(userRef, {
          isActive: newIsActive
        });
        
        // 화면 상태 즉시 반전 반영
        suspendTargetUser.value.isSuspended = !newIsActive;
        alert(newIsActive ? '계정이 정상적으로 활성화되었습니다.' : '계정이 정지 처리되었습니다.');
        
        // 회원관리 탭 등 데이터 새로고침
        refreshData(); 
      } catch (error) {
        console.error("계정 상태 변경 에러:", error);
        alert('상태 변경 중 오류가 발생했습니다.');
      }
    };

    const resetTargetUid = ref('');
    const isResetting = ref(false);

    const resetData = async (type) => {
      if (type === 'single') {
        if (!resetTargetUid.value) { alert('초기화할 회원의 UID를 입력해주세요.'); return; }
        if (!confirm(`⚠️ [경고] 정말 UID: ${resetTargetUid.value} 회원의 데이터를 초기화하시겠습니까?\n이 작업은 복구할 수 없습니다.`)) return;
      } else {
        const checkWord = prompt('⚠️ [경고] 전체 데이터를 초기화합니다.\n모든 매출, 수당 내역이 영구 삭제됩니다.\n진행하시려면 "초기화진행" 이라고 입력해주세요.');
        if (checkWord !== '초기화진행') {
          alert('입력 문구가 일치하지 않아 취소되었습니다.');
          return;
        }
      }

    isResetting.value = true;
      try {
        // ⭐️ 기존 auth 객체에서 앱 정보를 가져오고, 서울(asia-northeast3) 지역을 명시합니다.
        const functions = getFunctions(auth.app, 'asia-northeast3'); 
        const resetSystemData = httpsCallable(functions, 'resetSystemData');
        
        const result = await resetSystemData({ type, targetUid: resetTargetUid.value });
        alert(`✅ ${result.data.message}`);
        
        if (type === 'single') resetTargetUid.value = '';
        refreshData(); 
      } catch (error) {
        console.error('초기화 에러:', error);
        alert(`❌ 초기화 실패: ${error.message}`);
      } finally {
        isResetting.value = false;
      }
    };

    const searchTreeUser = async () => {
      let q = query(collection(db, 'users'), where('name', '==', treeSearchQuery.value));
      let snap = await getDocs(q);
      if (snap.empty) {
        q = query(collection(db, 'users'), where('email', '==', treeSearchQuery.value));
        snap = await getDocs(q);
      }
      if (snap.empty) {
        q = query(collection(db, 'users'), where('uid', '==', treeSearchQuery.value));
        snap = await getDocs(q);
      }

      if (!snap.empty) {
        treeRootUser.value = snap.docs[0].data();
        viewHistory.value = []; 
        await loadNetworkData(treeRootUser.value);
      } else {
        alert('회원을 찾을 수 없습니다.');
      }
    };

    const changeTreeTab = async (tab) => {
      adminTreeTab.value = tab;
      viewHistory.value = [];
      if (treeRootUser.value) {
        await loadNetworkData(treeRootUser.value);
      }
    };

    const fetchDescendants = async (uid, depth = 0, maxDepth = 10, nodesMap = new Map(), linksArr = []) => {
      if (depth >= maxDepth) return;
      const field = adminTreeTab.value === 'sponsor' ? 'supporter' : 'recommender';
      const q = query(collection(db, 'users'), where(field, '==', uid));
      const snap = await getDocs(q);

      for (const d of snap.docs) {
        const childData = d.data();
        if (!nodesMap.has(childData.uid)) {
          nodesMap.set(childData.uid, childData);
          linksArr.push({ source: uid, target: childData.uid, position: childData.position });
          await fetchDescendants(childData.uid, depth + 1, maxDepth, nodesMap, linksArr);
        }
      }
    };

    const loadNetworkData = async (rootNode) => {
      if (!rootNode) return;
      isLoadingTree.value = true;

      try {
        const nodesMap = new Map();
        const linksArr = [];
        nodesMap.set(rootNode.uid, rootNode);

        await fetchDescendants(rootNode.uid, 0, 10, nodesMap, linksArr);

        const nodes = Array.from(nodesMap.values()).map(d => ({
          id: d.uid,
          name: d.name,
          date: formatDate(d.createdAt),
          level: d.level,
          isRoot: d.uid === rootNode.uid,
          _raw: d
        }));

        graphData.value = markRaw({ nodes, links: linksArr });
        
        setTimeout(() => { renderD3Graph(); }, 100);
      } catch (error) {
        console.error('계보 데이터 로드 오류:', error);
      } finally {
        isLoadingTree.value = false;
      }
    };

    const renderD3Graph = () => {
      if (!d3Container.value) return;

      d3.select(d3Container.value).selectAll("*").remove();

      const width = d3Container.value.clientWidth || 800;
      const height = d3Container.value.clientHeight || 500;

      const data = {
        nodes: graphData.value.nodes.map(d => Object.create(d)),
        links: graphData.value.links.map(d => Object.create(d))
      };

      const svg = d3.select(d3Container.value)
        .append("svg")
        .attr("width", width)
        .attr("height", height)
        .attr("viewBox", [0, 0, width, height])
        .attr("style", "max-width: 100%; height: auto;");

      const g = svg.append("g");

      svg.call(d3.zoom()
        .extent([[0, 0], [width, height]])
        .scaleExtent([0.3, 3])
        .on("zoom", (event) => g.attr("transform", event.transform)));

      svg.append("defs").append("marker")
        .attr("id", "arrowhead")
        .attr("viewBox", "-0 -5 10 10")
        .attr("refX", 25)
        .attr("refY", 0)
        .attr("orient", "auto")
        .attr("markerWidth", 8)
        .attr("markerHeight", 8)
        .attr("xoverflow", "visible")
        .append("svg:path")
        .attr("d", "M 0,-5 L 10 ,0 L 0,5")
        .attr("fill", "#9ca3af")
        .style("stroke", "none");

      const simulation = d3.forceSimulation(data.nodes)
        .force("link", d3.forceLink(data.links).id(d => d.id).distance(120))
        .force("charge", d3.forceManyBody().strength(-800))
        .force("center", d3.forceCenter(width / 2, height / 2))
        .force("collide", d3.forceCollide().radius(50));

      const link = g.append("g")
        .selectAll("line")
        .data(data.links)
        .join("line")
        .attr("stroke", "#d1d5db")
        .attr("stroke-opacity", 0.8)
        .attr("stroke-width", 2)
        .attr("marker-end", "url(#arrowhead)");

      const drag = (simulation) => {
        function dragstarted(event) {
          if (!event.active) simulation.alphaTarget(0.3).restart();
          event.subject.fx = event.subject.x;
          event.subject.fy = event.subject.y;
        }
        function dragged(event) {
          event.subject.fx = event.x;
          event.subject.fy = event.y;
        }
        function dragended(event) {
          if (!event.active) simulation.alphaTarget(0);
          event.subject.fx = null;
          event.subject.fy = null;
        }
        return d3.drag().on("start", dragstarted).on("drag", dragged).on("end", dragended);
      };

      const node = g.append("g")
        .selectAll("g")
        .data(data.nodes)
        .join("g")
        .attr("cursor", "pointer")
        .call(drag(simulation))
        .on("click", (event, d) => {
          selectedTreeUser.value = d._raw;
          showTreeUserModal.value = true;
        });

      node.append("circle")
        .attr("r", d => d.isRoot ? 35 : 28)
        .attr("fill", d => d.isRoot ? '#1e2532' : (adminTreeTab.value === 'sponsor' ? (d._raw.position === 'LEFT' ? '#ef4444' : '#3b82f6') : '#22c55e'))
        .attr("stroke", "#ffffff")
        .attr("stroke-width", 3)
        .style("filter", "drop-shadow(0px 4px 6px rgba(0,0,0,0.1))");

      node.append("text")
        .attr("dy", "-0.2em")
        .attr("text-anchor", "middle")
        .attr("fill", "white")
        .attr("font-size", d => d.isRoot ? "14px" : "11px")
        .attr("font-weight", "bold")
        .text(d => d.name);

      node.append("text")
        .attr("dy", "1.2em")
        .attr("text-anchor", "middle")
        .attr("fill", "rgba(255,255,255,0.8)")
        .attr("font-size", "8px")
        .text(d => d.date);

      simulation.on("tick", () => {
        link.attr("x1", d => d.source.x).attr("y1", d => d.source.y).attr("x2", d => d.target.x).attr("y2", d => d.target.y);
        node.attr("transform", d => `translate(${d.x},${d.y})`);
      });
    };

    const drillDownTree = async (user) => {
      showTreeUserModal.value = false;
      viewHistory.value.push(treeRootUser.value);
      treeRootUser.value = user;
      await loadNetworkData(user);
    };

    const goUpTree = async () => {
      if (viewHistory.value.length === 0) return;
      const parentNode = viewHistory.value.pop();
      treeRootUser.value = parentNode;
      await loadNetworkData(parentNode);
    };

    const initCanvas = () => {
      if (!canvasRef.value) return; 
      ctx = canvasRef.value.getContext('2d'); 
      resizeCanvas(); 
      window.addEventListener('resize', resizeCanvas);
      for (let i = 0; i < particleCount; i++) { 
        particles.push({ 
          x: Math.random() * canvasRef.value.width, 
          y: Math.random() * canvasRef.value.height, 
          vx: (Math.random() - 0.5) * 0.6, 
          vy: (Math.random() - 0.5) * 0.6, 
          radius: Math.random() * 1.5 + 0.5 
        }); 
      } 
      animate();
    };

    const resizeCanvas = () => { 
      if (!canvasRef.value) return; 
      canvasRef.value.width = window.innerWidth; 
      canvasRef.value.height = window.innerHeight; 
    };

    const animate = () => {
      if (!canvasRef.value || !ctx) return; 
      ctx.clearRect(0, 0, canvasRef.value.width, canvasRef.value.height);
      for (let i = 0; i < particles.length; i++) {
        let p = particles[i]; 
        p.x += p.vx; 
        p.y += p.vy; 
        if (p.x < 0 || p.x > canvasRef.value.width) p.vx *= -1; 
        if (p.y < 0 || p.y > canvasRef.value.height) p.vy *= -1; 
        
        ctx.beginPath(); 
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2); 
        ctx.fillStyle = 'rgba(255, 204, 0, 0.5)'; 
        ctx.fill();
        
        for (let j = i + 1; j < particles.length; j++) { 
          let p2 = particles[j]; 
          let dx = p.x - p2.x; 
          let dy = p.y - p2.y; 
          let dist = Math.sqrt(dx * dx + dy * dy); 
          if (dist < 120) { 
            ctx.beginPath(); 
            ctx.moveTo(p.x, p.y); 
            ctx.lineTo(p2.x, p2.y); 
            ctx.strokeStyle = `rgba(255, 204, 0, ${0.8 - dist / 120})`; 
            ctx.lineWidth = 0.5; 
            ctx.stroke(); 
          } 
        }
      } 
      animationFrameId = requestAnimationFrame(animate);
    };

    onMounted(() => {
      loadCenters();
      refreshData();
      initCanvas();
    });

    onBeforeUnmount(() => {
      window.removeEventListener('resize', resizeCanvas);
      if (animationFrameId) cancelAnimationFrame(animationFrameId);
    });

return {
      canvasRef, menuList, currentMenu, rawData, filteredData, isLoading,
      searchQuery, searchCenter, searchDate, searchStatus, sortKey, sortOrder, sortBy, centers, newCenterName,
      refreshData, resetSearch, approveRequest, rejectRequest, completeWithdrawal,
      addCenter, removeCenter, formatDate, formatNumber, exportExcel, printPage,
      notices, noticeForm, editingNotice, saveNotice, editNotice, deleteNotice, resetNoticeForm,
      systemFee, saveSystemFee, suspendSearchQuery, searchSuspendUser, suspendTargetUser, toggleSuspend,
      resetTargetUid, isResetting, resetData,
      treeSearchQuery, treeRootUser, adminTreeTab, searchTreeUser, drillDownTree, goUpTree, changeTreeTab,
      isLoadingTree, d3Container, showTreeUserModal, selectedTreeUser, viewHistory,
      showUserEditModal, editUserData, openUserEditModal, closeUserEditModal, saveUserEdit,
      
      relSearchQuery, relSearchResult, searchRelationUser, 
      
      holdWithdrawal, refundWithdrawal,
      showCenterEditModal, editingCenter, centerUserSearchQuery, centerUserSearchResult, centerUserAccount, centerUserTargetRole, editingCenterIndex, openCenterEditModal, closeCenterEditModal, searchCenterUser, assignCenterUser, saveCenterEdit,
      showBonusModal, selectedBonus, editBonusAmount, openBonusModal, closeBonusModal, saveBonusEdit
    };
  }
}
</script>

<style scoped>
@media print {
  .no-print { display: none !important; }
  .print-area { padding: 0 !important; margin: 0 !important; width: 100% !important; overflow: visible !important; }
  .print-bg-white { background-color: white !important; box-shadow: none !important; border: none !important; }
  .print-block { display: block !important; }
  .print-overflow-visible { overflow: visible !important; }
  .print-table { width: 100% !important; border-collapse: collapse !important; }
  .print-table th, .print-table td { border: 1px solid #ccc !important; padding: 8px !important; color: black !important; }
}
text {
  user-select: none;
  pointer-events: none;
}
</style>